import { Fingerprint, ScanFace, CreditCard, KeyRound, Hand, HelpCircle, ArrowRight, ArrowLeft } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState, ErrorState, StatusBadge } from '@/components/common'
import { VERIFY_TYPES, PUNCH_STATES, type PunchView } from '../types'
import { cn } from '@/lib/utils'

/** Icon per verification method, so the feed is scannable without reading every row. */
const VERIFY_ICONS: Record<number, LucideIcon> = {
  0: KeyRound,
  1: Fingerprint,
  2: CreditCard,
  3: KeyRound,
  4: CreditCard,
  15: ScanFace,
  25: Hand,
}

/**
 * Punches as they arrive — the screen an administrator watches while commissioning a
 * terminal, to confirm that a scan at the door actually reaches the server.
 */
export function PunchFeed({
  punches,
  isLoading,
  error,
  onRetry,
}: {
  punches: PunchView[] | undefined
  isLoading?: boolean
  error?: unknown
  onRetry?: () => void
}) {
  if (isLoading) {
    return (
      <Card className="p-4">
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      </Card>
    )
  }

  if (error != null) {
    return <Card><ErrorState error={error} onRetry={onRetry} /></Card>
  }

  if (!punches || punches.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={Fingerprint}
          title="No punches yet"
          description="Scans appear here within seconds of happening. If a terminal is connected but nothing shows, check that its serial has been approved."
        />
      </Card>
    )
  }

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
        <p className="text-sm font-medium text-[var(--color-heading)]">Live punches</p>
        <span className="flex items-center gap-1.5 text-xs text-[var(--color-muted)]">
          <span className="size-2 animate-pulse rounded-full bg-[var(--color-success)]" aria-hidden="true" />
          Updating every 10 seconds
        </span>
      </div>

      <ul className="divide-y divide-[var(--color-border)]">
        {punches.map((punch) => {
          const VerifyIcon = VERIFY_ICONS[punch.verifyType ?? -1] ?? HelpCircle
          const isCheckIn = punch.punchState === 0 || punch.punchState === 3
          const DirectionIcon = isCheckIn ? ArrowRight : ArrowLeft

          return (
            <li key={punch.id} className="flex items-center gap-3 px-4 py-2.5">
              <span
                className={cn(
                  'grid size-9 shrink-0 place-items-center rounded-full',
                  punch.employeeId
                    ? 'bg-[var(--color-tint-green)] text-[var(--color-success)]'
                    : 'bg-[var(--color-tint-orange)] text-[var(--color-warning)]',
                )}
              >
                <VerifyIcon className="size-4" aria-hidden="true" />
              </span>

              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-[var(--color-heading)]">
                  {punch.employeeName ?? (
                    <>
                      PIN {punch.devicePin}{' '}
                      <span className="font-normal text-[var(--color-muted)]">— not mapped to an employee</span>
                    </>
                  )}
                </span>
                <span className="block truncate text-xs text-[var(--color-muted)]">
                  {punch.serialNumber} · {VERIFY_TYPES[punch.verifyType ?? -1] ?? 'Unknown method'}
                  {punch.punchState !== null && ` · ${PUNCH_STATES[punch.punchState] ?? 'Unknown direction'}`}
                </span>
              </span>

              <span className="flex shrink-0 items-center gap-3">
                {punch.processStatus === 'UNMATCHED' && <StatusBadge status="UNMATCHED" tone="warning" />}
                <DirectionIcon
                  className={cn(
                    'size-4 rtl-flip',
                    isCheckIn ? 'text-[var(--color-success)]' : 'text-[var(--color-muted)]',
                  )}
                  aria-hidden="true"
                />
                <time
                  className="w-[104px] text-end text-xs tabular-nums text-[var(--color-muted)]"
                  dateTime={punch.punchTime}
                >
                  {punch.punchTime.replace('T', ' ').slice(5, 16)}
                </time>
              </span>
            </li>
          )
        })}
      </ul>
    </Card>
  )
}
